/**
 * Step 3 — Review & Submit.
 * Themed rebuild of the old StepThree.tsx. Builds the exact same
 * dataMappingConfig.mappings.bidMultiplier.columnMapping shape (including
 * the "varialbes" typo in the key names) and modelParameterConfig.parameters
 * shape the backend (app/modules/bid_multiplier/bid_multiplier.py) expects —
 * uploads the file to S3, creates the jobConfig + jobRun, then hands off to
 * the shared detail page instead of an in-wizard results step.
 */

import React, { useState } from "react";
import { FileText, Rocket, SlidersHorizontal } from "lucide-react";
import axiosInstance from "../../../../utils/axiosInstance";
import { uploadFileToS3 } from "../../../../utils/s3Upload";
import { getTenantId } from "../../../../utils/auth";
import { JobType } from "../../../../constants/JobTypes";
import { UploadStepData } from "./StepUpload";
import { ConfigureStepData } from "./StepConfigure";

interface StepReviewProps {
  jobConfigId: string;
  uploadData: UploadStepData;
  configureData: ConfigureStepData;
  onPrevious: () => void;
  onCreated: () => void;
}

const OBJECTIVE_LABEL: Record<string, string> = { lead: "Lead Generation", sales: "Sales", reach: "Reach" };

const StepReview: React.FC<StepReviewProps> = ({ jobConfigId, uploadData, configureData, onPrevious, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState<"idle" | "uploading" | "creating">("idle");
  const [error, setError] = useState<string | null>(null);

  const variables = [
    { name: "Customer Identifier", value: configureData.userId, category: "Required" },
    { name: "Target Variable", value: configureData.targetVariable, category: "Required" },
    configureData.age && { name: "Age Range", value: configureData.age, category: "Demographic" },
    configureData.gender && { name: "Gender", value: configureData.gender, category: "Demographic" },
    configureData.locale && { name: "Locale", value: configureData.locale, category: "Demographic" },
    configureData.geoLocationType && { name: "Geo Location Type", value: configureData.geoLocationType === "dma" ? "DMA" : "Regions", category: "Geographic" },
    configureData.geoLocation && { name: "Geo Location", value: configureData.geoLocation, category: "Geographic" },
    configureData.devicePlatform && { name: "Device Platform", value: configureData.devicePlatform, category: "Device" },
    configureData.userOs && { name: "User OS", value: configureData.userOs, category: "Device" },
    configureData.userDevice && { name: "User Device", value: configureData.userDevice, category: "Device" },
  ].filter(Boolean) as { name: string; value: string; category: string }[];

  const categoryColor: Record<string, string> = {
    Required: "var(--blue)", Demographic: "#10b981", Geographic: "#06b6d4", Device: "#f59e0b",
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      setStage("uploading");
      await uploadFileToS3(uploadData.dataFile, `${jobConfigId}/input/bid_multiplier_data.csv`);

      setStage("creating");
      const payload = {
        jobConfigId,
        name: configureData.name,
        jobType: JobType.BidOptimizer,
        tenantId: getTenantId(),
        dataSourceConfig: { dataSourceType: "FILE_CSV" },
        dataTransformationConfig: {},
        dataMappingConfig: {
          mappings: {
            bidMultiplier: {
              columnMapping: {
                age_varialbes: configureData.age ? [configureData.age] : undefined,
                device_platform_varialbes: configureData.devicePlatform ? [configureData.devicePlatform] : undefined,
                gender_varialbes: configureData.gender ? [configureData.gender] : undefined,
                locale_varialbes: configureData.locale ? [configureData.locale] : undefined,
                region_varialbes: configureData.geoLocation ? [configureData.geoLocation] : undefined,
                user_devices_varialbes: configureData.userDevice ? [configureData.userDevice] : undefined,
                user_os_varialbes: configureData.userOs ? [configureData.userOs] : undefined,
                target_variables: configureData.targetVariable ? [configureData.targetVariable] : undefined,
              },
            },
          },
        },
        modelVersion: "V1",
        modelParameterConfig: {
          parameters: {
            campaignObjective: configureData.campaignObjective,
            geoLocationType: configureData.geoLocationType,
          },
        },
        modelOutputConfig: {},
      };

      await axiosInstance.post("/api/jobConfig", payload);
      await axiosInstance.post("/api/jobRun", { jobConfigId });

      onCreated();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      setError(`Failed to create job: ${msg}`);
      setSubmitting(false);
      setStage("idle");
    }
  };

  return (
    <div style={{ maxWidth: 920 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--text-primary)", margin: "0 0 6px" }}>
        Review and Submit
      </h2>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 20px", lineHeight: 1.5 }}>
        Review your configuration before submitting the job. Make sure all the selected variables are correct.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
        <SummaryCard icon={<FileText size={16} />} label="Data File" value={uploadData.dataFile.name} sub={`Size: ${(uploadData.dataFile.size / 1024).toFixed(2)} KB`} />
        <SummaryCard icon={<Rocket size={16} />} label="Job Name" value={configureData.name || "—"} sub={`Campaign Objective: ${OBJECTIVE_LABEL[configureData.campaignObjective] || configureData.campaignObjective}`} />
        <SummaryCard
          icon={<SlidersHorizontal size={16} />}
          label="Variables Selected"
          value={String(variables.length)}
          sub={`${variables.filter(v => v.category === "Required").length} required, ${variables.filter(v => v.category !== "Required").length} optional`}
        />
      </div>

      <div style={{ marginBottom: 20, padding: 18, borderRadius: "var(--radius)", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 }}>Selected Variables</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ width: "33.33%", textAlign: "left", padding: "0 10px 10px", color: "var(--text-primary)", fontWeight: 600, fontSize: 12, borderBottom: "1px solid var(--border-default)" }}>Variable</th>
              <th style={{ width: "33.33%", textAlign: "left", padding: "0 10px 10px", color: "var(--text-primary)", fontWeight: 600, fontSize: 12, borderBottom: "1px solid var(--border-default)" }}>Selected Column</th>
              <th style={{ width: "33.33%", textAlign: "left", padding: "0 10px 10px", color: "var(--text-primary)", fontWeight: 600, fontSize: 12, borderBottom: "1px solid var(--border-default)" }}>Category</th>
            </tr>
          </thead>
          <tbody>
            {variables.map(v => (
              <tr key={v.name}>
                <td style={{ width: "33.33%", padding: "8px 10px", color: "var(--text-primary)", fontWeight: 500, borderBottom: "1px solid var(--border-subtle)" }}>{v.name}</td>
                <td style={{ width: "33.33%", padding: "8px 10px", color: "var(--text-secondary)", fontFamily: "var(--font-mono)", borderBottom: "1px solid var(--border-subtle)" }}>{v.value}</td>
                <td style={{ width: "33.33%", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border-subtle)" }}>
                  <span style={{ fontSize: 10, color: "#fff", background: categoryColor[v.category], padding: "2px 7px", borderRadius: 10 }}>
                    {v.category}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: 20, padding: 18, borderRadius: "var(--radius)", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 }}>Data Preview</div>
        <div style={{ overflowX: "auto", maxHeight: 200 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {uploadData.headers.map(h => (
                  <th key={h} style={{ position: "sticky", top: 0, textAlign: "left", padding: "8px 10px", background: "var(--bg-hover)", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-default)", whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uploadData.previewData.slice(0, 5).map((row, i) => (
                <tr key={i}>
                  {uploadData.headers.map(h => (
                    <td key={h} style={{ padding: "7px 10px", color: "var(--text-primary)", borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap" }}>
                      {String(row[h] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ textAlign: "right", marginTop: 8, fontSize: 11, color: "var(--text-tertiary)" }}>Showing first 5 rows of data</div>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: "var(--radius)", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", color: "#ef4444", fontSize: 12 }}>
          {error}
        </div>
      )}

      {submitting && (
        <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: "var(--radius)", background: "var(--blue-subtle)", border: "1px solid var(--border-default)", fontSize: 12.5, color: "var(--blue)" }}>
          {stage === "uploading" ? "Uploading data file…" : "Creating job…"}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={onPrevious} disabled={submitting} style={secondaryBtnStyle}>← Previous</button>
        <button onClick={handleSubmit} disabled={submitting} style={primaryBtnStyle}>
          {submitting ? "Creating…" : "Create Bid Optimizer Job"}
        </button>
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string }> = ({ icon, label, value, sub }) => (
  <div style={{ padding: 14, borderRadius: "var(--radius)", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-secondary)", marginBottom: 4 }}>
      {icon}
      <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: sub ? 4 : 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>{sub}</div>}
  </div>
);

const primaryBtnStyle: React.CSSProperties = {
  padding: "9px 20px", borderRadius: "var(--radius)", background: "var(--blue)",
  color: "#fff", border: "none", fontSize: 13, fontWeight: 500, cursor: "pointer",
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: "9px 20px", borderRadius: "var(--radius)", background: "var(--bg-hover)",
  color: "var(--text-secondary)", border: "1px solid var(--border-default)", fontSize: 13, cursor: "pointer",
};

export default StepReview;
